import type { Maybe } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-tailwind-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import '@chancenwerk/client/components/chancen-component-card-title.js';
import { projectConfig } from '@chancenwerk/config/config.js';
import '@chancenwerk/client/design/components/cr-dropdown-selection.js';
import { MarktAkquiseContactRecord } from '@chancenwerk/markt/entities/MarktAkquiseContactRecord.js';
import { marktAkquiseStatusColor, marktAkquiseStatusText } from '@chancenwerk/markt/entities/MarktAkquiseStatus.js';
import { RaumRoutes } from '@chancenwerk/routes/raumRoutes.js';
import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { of } from 'rxjs';
import { MarktDialogContactRecordEdit } from '../components/dialogs/markt-dialog-contact-record-edit.js';

/**
 * @customElement markt-component-contact-manager
 */
@customElement('markt-component-contact-manager')
export class MarktComponentContactManager extends ChemistryLitElement {
  @property() contacts = new RXController(this, [] as MarktAkquiseContactRecord[]);

  override render() {
    return html`<cr-flex space="md">
      <cr-flex
        horizontal
        padding="lg"
        space="xxl"
        space-between
        crossaxis-center
        ${css({
          backgroundColor: 'rgba(0,0,0,0.05)',
          borderRadius: this.sizes.borderRadiusSecondary,
          paddingTop: this.spacing.sm,
          paddingBottom: this.spacing.sm,
        })}
      >
        <cr-text bold>${this.contacts.value.length} Kontakte</cr-text>
        <chancen-component-context-menu
          .items=${[
            {
              icon: 'add',
              label: 'Hinzufügen',
              action: async () => {
                const res = await MarktDialogContactRecordEdit.showPopup<Maybe<MarktAkquiseContactRecord>>({
                  props: {},
                });

                if (res) {
                  this.dispatchEvent(new CustomEvent('record-added', { detail: { value: res } }));
                }
              },
            },
          ]}
        ></chancen-component-context-menu>
      </cr-flex>
      <cr-data-view
        .options=${{
          class: MarktAkquiseContactRecord,
          operation: (skip, limit) => {
            return () => {
              if (skip > 0) {
                return of([]);
              }
              return of(this.contacts.value ?? []);
            };
          },
          isEntryClickable: false,
          onlyShowLastTranslation: true,
          customColumns: [
            {
              customID: 'von',
              columnName: 'von',
              columnWidth: 'max-content',
              getFieldMapping: (record: MarktAkquiseContactRecord) => {
                return html`<cr-flex
                  horizontal
                  crossaxis-center
                  ${css({
                    backgroundColor: this.colors.primary.shade(200),
                    paddingRight: this.spacing.sm,
                    borderRadius: this.sizes.borderRadiusSecondary,
                    margin: this.spacing.sm,
                  })}
                >
                  <cr-button
                    icon="person"
                    mode="icon"
                    href="${`${projectConfig.get('RAUM_URL')}${RaumRoutes.editEmployee.substring(1)}/${
                      record.from?._id
                    }`}"
                  ></cr-button>
                  <cr-text>${record.from?.toString()}</cr-text>
                </cr-flex>`;
              },
            },
            {
              customID: 'an',
              columnName: 'an',
              columnWidth: 'max-content',
              getFieldMapping: (record: MarktAkquiseContactRecord) => {
                return html`<cr-flex
                  horizontal
                  crossaxis-center
                  ${css({
                    backgroundColor: this.colors.primary.shade(200),
                    paddingRight: this.spacing.sm,
                    borderRadius: this.sizes.borderRadiusSecondary,
                    margin: this.spacing.sm,
                  })}
                >
                  <cr-button
                    icon="person"
                    mode="icon"
                    href="${`${projectConfig.get('RAUM_URL')}${RaumRoutes.editEmployee.substring(1)}/${
                      record.to?._id
                    }`}"
                  ></cr-button>
                  <cr-text>${record.to?.toString()}</cr-text>
                </cr-flex>`;
              },
            },
            {
              customID: 'when',
              columnName: 'wann',
              columnWidth: '150px',
              getFieldMapping: (record: MarktAkquiseContactRecord) => {
                return record.when?.toFormat('dd.MM.yyyy');
              },
            },
            {
              customID: 'antwort',
              columnName: 'Antwort',
              columnWidth: '1fr',
              getFieldMapping: (record: MarktAkquiseContactRecord) => {
                return html`<cr-text ${css({ padding: this.spacing.sm })}>
                  ${record.response ?? 'Noch keine...'}
                </cr-text>`;
              },
            },
            {
              customID: 'nextStep',
              columnName: 'nächster Schritt',
              columnWidth: '1fr',
              getFieldMapping: (record: MarktAkquiseContactRecord) => {
                return html`<cr-text ${css({ padding: this.spacing.sm })}>
                  ${record.nextStep ?? 'Noch keine...'}
                </cr-text>`;
              },
            },
            {
              customID: 'nextStepWhen',
              columnName: 'wann',
              columnWidth: '150px',
              getFieldMapping: (record: MarktAkquiseContactRecord) => {
                return record.nextStepWhen?.toFormat('dd.MM.yyyy');
              },
            },
            {
              customID: 'Status',
              columnName: 'status',
              columnWidth: '150px',
              getFieldMapping: (record: MarktAkquiseContactRecord) => {
                return html`<cr-text
                  bold
                  ${css({
                    borderRadius: this.sizes.borderRadiusSecondary,
                    padding: this.spacing.sm,
                    backgroundColor: marktAkquiseStatusColor(record.status),
                    textAlign: 'center',
                    color: 'white',
                  })}
                >
                  ${marktAkquiseStatusText(record.status ?? 'NEW')}
                </cr-text>`;
              },
            },
            {
              customID: 'edit',
              columnName: '',
              columnWidth: 'max-content',
              getFieldMapping: (record: MarktAkquiseContactRecord) => {
                return html`<cr-flex padding="xs" horizontal space="md">
                  <cr-button
                    mode="icon"
                    icon="edit"
                    @click=${async () => {
                      const res = await MarktDialogContactRecordEdit.showPopup<Maybe<MarktAkquiseContactRecord>>({
                        props: { record },
                      });

                      if (res) {
                        this.dispatchEvent(new CustomEvent('record-edited', { detail: { value: res } }));
                      }
                    }}
                  ></cr-button>
                  <cr-button
                    mode="icon"
                    tone="error"
                    icon="delete"
                    @click=${async () => {
                      this.dispatchEvent(new CustomEvent('record-deleted', { detail: { value: record } }));
                    }}
                  ></cr-button>
                </cr-flex>`;
              },
            },
          ],
        } as DataViewOptions<typeof MarktAkquiseContactRecord>}
      ></cr-data-view>
    </cr-flex>`;
  }
}
